/**
 * useApprovalsBadge — polled count of pending approvals for the module rail.
 *
 * Cadence: 60s default. The hook caches between mounts so the rail badge keeps
 * its value while the user navigates, and only re-fires the network on the
 * cadence interval. When `urgent: true` (or due-by < 60min) the badge tone
 * flips to `warn` so the rail shows the red ring.
 */

import { useEffect, useState } from 'react';
import { fetchApprovals, type GraphApproval } from '../services/graphApprovalsClient';

export interface ApprovalsBadgeState {
  count: number;
  urgent: boolean;
  /** True while the very first fetch is outstanding. */
  loading: boolean;
}

const POLL_MS = 60_000;

interface CacheEntry {
  state: ApprovalsBadgeState;
  fetchedAt: number;
}

let cache: CacheEntry | null = null;
const subscribers = new Set<(s: ApprovalsBadgeState) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let inFlight = false;
// 2026-07-02 — circuit breaker: this poll 401'd every 60s forever in
// environments where the Graph approvals audience isn't granted. After 3
// consecutive failures the interval stops until the next fresh mount.
let consecutiveFailures = 0;

async function refreshOnce(): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  try {
    const { approvals } = await fetchApprovals({ status: 'pending', top: 100 });
    const pending: GraphApproval[] = approvals.filter((a) => a.overallStatus === 'pending');
    const next: ApprovalsBadgeState = {
      count: pending.length,
      urgent: pending.some((a) => a.urgent),
      loading: false,
    };
    cache = { state: next, fetchedAt: Date.now() };
    subscribers.forEach((fn) => fn(next));
    consecutiveFailures = 0;
  } catch {
    // Silent — keep previous state. Badge is best-effort.
    consecutiveFailures += 1;
    if (consecutiveFailures >= 3 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  } finally {
    inFlight = false;
  }
}

function ensurePolling(): void {
  if (timer !== null) return;
  void refreshOnce();
  timer = setInterval(() => {
    void refreshOnce();
  }, POLL_MS);
}

function teardownIfIdle(): void {
  if (subscribers.size > 0) return;
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

export function useApprovalsBadge(): ApprovalsBadgeState {
  const [state, setState] = useState<ApprovalsBadgeState>(
    () => cache?.state ?? { count: 0, urgent: false, loading: true },
  );

  useEffect(() => {
    subscribers.add(setState);
    ensurePolling();
    if (cache) setState(cache.state);
    return () => {
      subscribers.delete(setState);
      teardownIfIdle();
    };
  }, []);

  return state;
}

/** Force an immediate refresh — call after a decision POST. */
export function refreshApprovalsBadge(): void {
  void refreshOnce();
}
